import { useCallback, useMemo } from 'react';

import {
  useGetConfigurationProcesses,
  useGetConfigurationFeatures,
} from '@/api/REST/configuration';
import { useGetProjectById } from '@/api/typesense/projects/useGetProjectById';
import { useHasPermissionToAccessPage } from '@/hooks/useHasPermissionToAccessPage';

const PAGE_PERMISSIONS = {
  planning: 'ProjectTaskPlanHour',
  financials: 'ProjectPaymentPlan',
  team: 'ProjectResourceGroup',
  billing: 'ProjectInvoiceOverview',
  settings: 'ProjectSetting',
} as const;

export const useProjectPermissions = (projectId: string) => {
  const { data: project, isLoading: isProjectLoading } =
    useGetProjectById(projectId);
  const { data: processes, isLoading: isProcessesLoading } =
    useGetConfigurationProcesses();
  const { data: features, isLoading: isFeaturesLoading } =
    useGetConfigurationFeatures();

  // Check all permissions
  const { hasPermission: hasPlanningPermission, isLoading: isPlanningLoading } =
    useHasPermissionToAccessPage(PAGE_PERMISSIONS.planning);
  const {
    hasPermission: hasFinancialsPermission,
    isLoading: isFinancialsLoading,
  } = useHasPermissionToAccessPage(PAGE_PERMISSIONS.financials);
  const { hasPermission: hasTeamPermission, isLoading: isTeamLoading } =
    useHasPermissionToAccessPage(PAGE_PERMISSIONS.team);
  const { hasPermission: hasBillingPermission, isLoading: isBillingLoading } =
    useHasPermissionToAccessPage(PAGE_PERMISSIONS.billing);
  const { hasPermission: hasSettingsPermission, isLoading: isSettingsLoading } =
    useHasPermissionToAccessPage(PAGE_PERMISSIONS.settings);

  const isInvoicingEnabled = processes?.isInvoicingEnabled ?? false;
  const isProjectContractsEnabled = features?.projectContracts ?? false;
  // Only determine if internal when project exists to prevent UI flicker
  const isInternalProject = project?.IsInternalProject;

  // Determine which tabs are available
  const availableTabs = useMemo(
    () => ({
      planning: hasPlanningPermission,
      // Only show financials/billing tabs when we know the project type
      financials:
        project !== undefined &&
        !isInternalProject &&
        hasFinancialsPermission &&
        isProjectContractsEnabled,
      team: hasTeamPermission,
      billing:
        project !== undefined &&
        !isInternalProject &&
        hasBillingPermission &&
        isInvoicingEnabled,
      settings: hasSettingsPermission,
    }),
    [
      project,
      isInternalProject,
      isInvoicingEnabled,
      isProjectContractsEnabled,
      hasPlanningPermission,
      hasFinancialsPermission,
      hasTeamPermission,
      hasBillingPermission,
      hasSettingsPermission,
    ],
  );

  // Get first available tab based on priority order
  const getDefaultTab = useCallback(() => {
    if (availableTabs.planning) return 'planning';
    if (availableTabs.financials) return 'financials';
    if (availableTabs.team) return 'team';
    if (availableTabs.billing) return 'billing';
    if (availableTabs.settings) return 'settings';
    // Fallback - should never happen in production
    return null;
  }, [availableTabs]);

  return {
    availableTabs,
    getDefaultTab,
    isLoading:
      isProjectLoading ||
      isProcessesLoading ||
      isFeaturesLoading ||
      isPlanningLoading ||
      isFinancialsLoading ||
      isTeamLoading ||
      isBillingLoading ||
      isSettingsLoading,
    project,
  };
};
